import type {
  AccountsPayableDueChip,
  AccountsPayableSort,
  AccountsPayableStatus,
  ListAccountsPayablesParams,
} from '../../lib/accountsPayableApi';

/**
 * The filters of the Accounts Payable bill list (D49 phase 2), as the page holds them, as the
 * URL carries them, and as the API takes them.
 *
 * The URL is how a report DRILLS DOWN: a figure on the aging report is a link to
 * `/accounts?tab=payable&supplier=..&supplierExact=1&currency=TTD&dueFrom=..&dueTo=..&held=exclude`,
 * and the page reads its starting state from that. Every rule (which bills a column holds, what
 * is overdue) is the server's; nothing in this file decides one. It only carries values.
 */

export interface PayableFilters {
  status: AccountsPayableStatus | 'all';
  supplier: string;
  /** `supplier` is a stored name to match exactly (a link from a report row), not a search. */
  supplierExact: boolean;
  q: string;
  dueFrom: string;
  dueTo: string;
  invoiceFrom: string;
  invoiceTo: string;
  minOutstanding: string;
  maxOutstanding: string;
  currency: string;
  due: AccountsPayableDueChip | '';
  /** `only` = on hold or disputed; `exclude` = everything else. Set by a report link. */
  held: '' | 'only' | 'exclude';
  /** Only bills that still owe something. Set by a report link. */
  openOnly: boolean;
  sort: AccountsPayableSort;
  dir: 'asc' | 'desc';
}

export const DEFAULT_PAYABLE_FILTERS: PayableFilters = Object.freeze({
  status: 'all',
  supplier: '',
  supplierExact: false,
  q: '',
  dueFrom: '',
  dueTo: '',
  invoiceFrom: '',
  invoiceTo: '',
  minOutstanding: '',
  maxOutstanding: '',
  currency: '',
  due: '',
  held: '',
  openOnly: false,
  sort: 'dueDate',
  dir: 'asc',
}) as PayableFilters;

export const DUE_CHIP_OPTIONS: ReadonlyArray<{ value: AccountsPayableDueChip; label: string }> = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'due_today', label: 'Due today' },
  { value: 'due_7', label: 'Due in 7 days' },
  { value: 'due_30', label: 'Due in 30 days' },
  { value: 'no_due_date', label: 'No due date' },
];

export const SORT_OPTIONS: ReadonlyArray<{ value: AccountsPayableSort; label: string }> = [
  { value: 'dueDate', label: 'Due date' },
  { value: 'invoiceDate', label: 'Invoice date' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'totalAmount', label: 'Total amount' },
  { value: 'supplier', label: 'Supplier' },
];

const STATUSES: ReadonlyArray<AccountsPayableStatus | 'all'> = [
  'all',
  'unpaid',
  'partially_paid',
  'paid',
  'overdue',
  'on_hold',
  'disputed',
];

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const AMOUNT_RE = /^\d+(\.\d+)?$/;
const CURRENCY_RE = /^[A-Za-z]{3}$/;
const FLAG_ON = new Set(['1', 'true']);

/**
 * The starting filters from a URL's query string. A value that is not one this page knows is
 * dropped to the default rather than sent on: a hand-edited or stale link opens the plain list,
 * never an error. (The server still validates everything it is sent.)
 */
export function parsePayableFilters(search: string): PayableFilters {
  const qs = new URLSearchParams(search || '');
  const text = (key: string) => qs.get(key) ?? '';
  const day = (key: string) => (DAY_RE.test(text(key)) ? text(key) : '');
  const amount = (key: string) => (AMOUNT_RE.test(text(key).trim()) ? text(key).trim() : '');

  const status = text('status') as PayableFilters['status'];
  const due = text('due') as AccountsPayableDueChip;
  const held = text('held');
  const sort = text('sort') as AccountsPayableSort;

  return {
    status: STATUSES.includes(status) ? status : 'all',
    supplier: text('supplier'),
    supplierExact: FLAG_ON.has(text('supplierExact')),
    q: text('q'),
    dueFrom: day('dueFrom'),
    dueTo: day('dueTo'),
    invoiceFrom: day('invoiceFrom'),
    invoiceTo: day('invoiceTo'),
    minOutstanding: amount('minOutstanding'),
    maxOutstanding: amount('maxOutstanding'),
    currency: CURRENCY_RE.test(text('currency')) ? text('currency').toUpperCase() : '',
    due: DUE_CHIP_OPTIONS.some((o) => o.value === due) ? due : '',
    held: held === 'only' || held === 'exclude' ? held : '',
    openOnly: FLAG_ON.has(text('openOnly')),
    sort: SORT_OPTIONS.some((o) => o.value === sort) ? sort : DEFAULT_PAYABLE_FILTERS.sort,
    dir: text('dir') === 'desc' ? 'desc' : 'asc',
  };
}

/** The list API's params for these filters (paging is the caller's). */
export function payableFiltersToParams(filters: PayableFilters): Omit<ListAccountsPayablesParams, 'page' | 'limit'> {
  return {
    status: filters.status,
    supplier: filters.supplierExact ? filters.supplier : filters.supplier.trim(),
    supplierExact: filters.supplierExact,
    q: filters.q.trim(),
    dueFrom: filters.dueFrom,
    dueTo: filters.dueTo,
    invoiceFrom: filters.invoiceFrom,
    invoiceTo: filters.invoiceTo,
    minOutstanding: filters.minOutstanding.trim(),
    maxOutstanding: filters.maxOutstanding.trim(),
    currency: filters.currency,
    openOnly: filters.openOnly,
    held: filters.held || undefined,
    due: filters.due || undefined,
    sort: filters.sort,
    dir: filters.dir,
  };
}

/** Which of these are set to something other than the plain list (the sort excluded). */
export function activeFilterKeys(filters: PayableFilters): Array<keyof PayableFilters> {
  const keys: Array<keyof PayableFilters> = [
    'dueFrom',
    'dueTo',
    'invoiceFrom',
    'invoiceTo',
    'minOutstanding',
    'maxOutstanding',
    'currency',
    'due',
    'held',
    'openOnly',
  ];
  return keys.filter((key) => filters[key] !== DEFAULT_PAYABLE_FILTERS[key]);
}

/**
 * A query string (`?tab=payable&...`) for these filters, writing only what differs from the
 * plain list. `parsePayableFilters` of the result gives the filters back.
 */
export function payableFiltersToSearch(filters: PayableFilters): string {
  const qs = new URLSearchParams();
  qs.set('tab', 'payable');
  if (filters.status !== 'all') qs.set('status', filters.status);
  // An exact supplier with a blank name means "bills with no supplier name", so it is kept.
  if (filters.supplier !== '' || filters.supplierExact) qs.set('supplier', filters.supplier);
  if (filters.supplierExact) qs.set('supplierExact', '1');
  if (filters.q !== '') qs.set('q', filters.q);
  const plain: Array<keyof PayableFilters> = [
    'dueFrom',
    'dueTo',
    'invoiceFrom',
    'invoiceTo',
    'minOutstanding',
    'maxOutstanding',
    'currency',
    'due',
    'held',
  ];
  for (const key of plain) {
    const value = filters[key];
    if (typeof value === 'string' && value !== '') qs.set(key, value);
  }
  if (filters.openOnly) qs.set('openOnly', '1');
  if (filters.sort !== DEFAULT_PAYABLE_FILTERS.sort) qs.set('sort', filters.sort);
  if (filters.dir !== DEFAULT_PAYABLE_FILTERS.dir) qs.set('dir', filters.dir);
  return `?${qs.toString()}`;
}

/** The bill list, opened with these filters set. What every report figure links to. */
export function payableListHref(filters: Partial<PayableFilters>): string {
  return `/accounts${payableFiltersToSearch({ ...DEFAULT_PAYABLE_FILTERS, ...filters })}`;
}

/**
 * The filters with everything the filter bar owns put back: the dates, amounts, currency, the
 * due chip and what a report link set. The search box, the supplier box, the status control and
 * the sort each have their own control and are left as they are.
 */
export function clearedBarFilters(filters: PayableFilters): PayableFilters {
  return {
    ...filters,
    supplierExact: false,
    dueFrom: '',
    dueTo: '',
    invoiceFrom: '',
    invoiceTo: '',
    minOutstanding: '',
    maxOutstanding: '',
    currency: '',
    due: '',
    held: '',
    openOnly: false,
  };
}
